import { type TDashboardTool } from "@/api/dashboard";
import { AppCard } from "@/components/ui";
import { Skeleton } from "@/components/ui/Skeleton";

function ToolCard({ tool }: { tool: TDashboardTool }) {
  return (
    <AppCard className="p-4 transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground sm:text-base">{tool.name}</h3>
          <p className="mt-2 line-clamp-2 text-xs text-muted-foreground sm:text-sm">{tool.description}</p>
        </div>
        <a
          href={tool.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 text-xs font-semibold text-primary hover:underline sm:text-sm"
        >
          Open
        </a>
      </div>
    </AppCard>
  );
}

export function ToolsGridSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {[1, 2, 3, 4].map((i) => (
        <AppCard key={i} className="space-y-3 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-4/5" />
            </div>
            <Skeleton className="h-4 w-10 flex-shrink-0" />
          </div>
        </AppCard>
      ))}
    </div>
  );
}

type TToolsGridProps = {
  tools: TDashboardTool[];
  emptyMessage: string;
  hideEmptyState?: boolean;
};

export function ToolsGrid({ tools, emptyMessage, hideEmptyState = false }: TToolsGridProps) {
  if (tools.length > 0) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        {tools.map((tool) => (
          <ToolCard
            key={`${tool.name}-${tool.url}`}
            tool={tool}
          />
        ))}
      </div>
    );
  }
  if (hideEmptyState) {
    return null;
  }
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card p-4 text-center text-xs text-muted-foreground sm:text-sm">
      {emptyMessage}
    </div>
  );
}
